/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import { isStoreInfoLocation } from '@msdyn365-commerce-modules/bopis-utilities';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import { observable, when, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
// Remove the static import of azure-maps-control
import {
    ArrayExtensions,
    CrossEntityNodeType,
    getCrossEntityNodeKey,
    ICrossEntityInventoryNode
} from '@msdyn365-commerce-modules/retail-actions';
// Import the types only from azure-maps-control
// eslint-disable-next-line prettier/prettier
import type * as atlas from 'azure-maps-control';

import { IAzureMapsData } from './azure-maps.data';
import { IAzureMapsProps, IPushpinOptionsData } from './azure-maps.props.autogenerated';

const ARIA_ID_REFERENCE_ATTRIBUTES = ['aria-controls', 'aria-describedby', 'aria-details', 'aria-labelledby', 'aria-owns'];

/**
 * Namespaces the IDs emitted by the Azure Maps SDK and their ARIA references.
 * The SDK uses fixed IDs for every map instance, which creates ambiguous references when a page contains multiple maps.
 * @param container - The map element populated by the SDK.
 * @param namespace - A page-unique module identifier.
 */
export const namespaceMapAriaIds = (container: HTMLElement, namespace: string): void => {
    const normalizedNamespace = namespace.replace(/[^A-Za-z0-9_-]/g, '-');
    const idMap = new Map<string, string>();

    container.querySelectorAll<HTMLElement>('[id^="atlas-map-"]').forEach(element => {
        const currentId = element.id;
        const namespacedId = `${normalizedNamespace}-${currentId}`;
        idMap.set(currentId, namespacedId);
        element.id = namespacedId;
    });

    if (idMap.size === 0) {
        return;
    }

    container.querySelectorAll<HTMLElement>('*').forEach(element => {
        ARIA_ID_REFERENCE_ATTRIBUTES.forEach(attribute => {
            const reference = element.getAttribute(attribute);
            if (!reference) {
                return;
            }

            const namespacedReference = reference
                .split(/\s+/)
                .map(id => idMap.get(id) ?? id)
                .join(' ');
            element.setAttribute(attribute, namespacedReference);
        });
    });
};

/**
 * Header Interface.
 */
export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
}

/**
 * AzureMapViewProps Interface.
 */
export interface IAzureMapViewProps extends IAzureMapsProps<IAzureMapsData> {
    ModuleProps?: IModuleProps;
    Header?: IHeader;
    MapProps?: INodeProps;
    Map?: atlas.Map | undefined; // Change from Microsoft.Maps.Map to atlas.Map
}

/**
 * AzureMap component.
 * @extends {React.Component<IAzureMapsProps<IAzureMapsData>>}
 */
@observer
class AzureMap extends React.Component<IAzureMapsProps<IAzureMapsData>> {
    private static readonly TIMEOUT: number = 500;

    @observable public map: atlas.Map | undefined;

    private readonly mapRef: React.RefObject<HTMLElement> = React.createRef<HTMLElement>();

    public constructor(props: IAzureMapsProps<IAzureMapsData>) {
        super(props);
    }

    public componentDidMount(): void {
        this._loadCss();
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            }
        } = this.props;

        if (channel && !channel.AzureMapsApiKey) {
            telemetry.error('AzureMapsApiKey is missing.');
            return;
        }

        if (channel && !channel.AzureMapsEnabled) {
            telemetry.error('Map is disabled from HQ.');
            return;
        }
        const loadMapAPIInput = {
            key: channel?.AzureMapsApiKey,
            lang: this.props.context.actionContext.requestContext.locale,
            market: this.props.context?.actionContext?.requestContext?.channel?.ChannelCountryRegionISOCode,
            AzureMapsEnabled: channel?.AzureMapsEnabled,
        };

        when(
            () => !!this.props.data.storeSelectorStateManager?.result?.setMapModuleLoaded,
            () => {
                this.props.data.storeSelectorStateManager?.result?.setMapModuleLoaded(true);
            }
        );

        when(
            () => !!this.props.data.distributorSelectorStateManager?.result?.setMapModuleLoaded,
            () => {
                this.props.data.distributorSelectorStateManager?.result?.setMapModuleLoaded(true);
            }
        );

        reaction(
            () =>
                this.props.data.storeSelectorStateManager?.result?.loadMapApi ||
                this.props.data.distributorSelectorStateManager?.result?.loadMapApi,
            () => {
                if (this.props.data.distributorSelectorStateManager?.result?.loadMapApi) {
                    this.props.data.distributorSelectorStateManager?.result?.loadMapApi(loadMapAPIInput);
                } else {
                    this.props.data.storeSelectorStateManager?.result?.loadMapApi(loadMapAPIInput);
                }
            }
        );

        import('azure-maps-control')
            .then(atlas => {
                const azureMapsAuthOptions: atlas.AuthenticationOptions = {
                    authType: atlas.AuthenticationType.subscriptionKey,
                    subscriptionKey: channel?.AzureMapsApiKey
                };

                this._initMap(azureMapsAuthOptions, atlas);

                if (this.props.data.distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen) {
                    this._updateMapsForDistributor(atlas);
                } else if (this._isWarehouseMapActive()) {
                    this._updateMapForWarehouses(atlas);
                } else {
                    this._updateMap(atlas);
                }

                reaction(
                    () => {
                        return [
                            this.props.data.storeSelectorStateManager?.result?.context?.orgUnitStoreInformation,
                            this.props.data.storeSelectorStateManager?.result?.selectedStoreLocationId,
                            this.map
                        ];
                    },
                    () => {
                        this._updateMap(atlas);
                    }
                );

                reaction(
                    () => {
                        return [
                            this.props.data.distributorSelectorStateManager?.result?.distributorList,
                            this.props.data.distributorSelectorStateManager?.result?.selectedDistributorId,
                            this.map
                        ];
                    },
                    () => {
                        this._updateMapsForDistributor(atlas);
                    }
                );

                reaction(
                    () => {
                        return [
                            this.props.data.warehouseLocatorStateManager?.result?.nodes,
                            this.props.data.warehouseLocatorStateManager?.result?.selectedNodeKey,
                            this.map
                        ];
                    },
                    () => {
                        this._updateMapForWarehouses(atlas);
                    }
                );
            })
            .catch(error => {
                telemetry.error('Error loading azure-maps-control:', error);
                console.error('Error loading azure-maps-control:', error);
            });

    }

    public render(): JSX.Element | null {
        const {
            data: { distributorSelectorStateManager, storeSelectorStateManager },
            config: { heading, className }
        } = this.props;

        const distributorStatusSuccess = 'SUCCESS';
        const shouldDisplayMap = storeSelectorStateManager?.result?.isDialogOpen
            ? storeSelectorStateManager.result.listMapViewState.displayMap
            : distributorSelectorStateManager?.status === distributorStatusSuccess &&
              distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen
            ? distributorSelectorStateManager.result.listMapViewState.displayMap
            : true;

        const viewProps: IAzureMapViewProps = {
            ...this.props,
            ModuleProps: {
                tag: 'div',
                moduleProps: this.props,
                className: classname('ms-azure-maps', { show: shouldDisplayMap }, className)
            },
            Header: {
                headerProps: { className: 'ms-azure-maps__header' },
                heading: heading?.text && (
                    <Msdyn365.Text
                        className='ms-azure-maps__heading'
                        {...heading}
                        tag={heading.tag || 'h2'}
                        text={heading.text}
                        editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                    />
                )
            },
            MapProps: {
                tag: 'div',
                className: 'ms-azure-maps__body',
                ref: this.mapRef,
                style: { height: '650px' }
            },
            Map: this.map
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Method to be called on heading change.
     * @param event -Content Edit Event.
     */
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * Initialize the map.
     */
    private readonly _initMap = (azureMapsAuthOptions: atlas.AuthenticationOptions, atlas: typeof import('azure-maps-control')) => {
        const { data } = this.props;

        this.map = new atlas.Map(this.mapRef.current as HTMLElement, azureMapsAuthOptions);
        namespaceMapAriaIds(this.mapRef.current as HTMLElement, this.props.id);
        this.map.events.add('ready', () => {
            namespaceMapAriaIds(this.mapRef.current as HTMLElement, this.props.id);
            this.map?.controls.add(new atlas.control.ZoomControl(), {
                position: atlas.ControlPosition.TopRight
            });
        });
        this.map.events.add('click', async () => {
            if (data.distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen) {
                data.distributorSelectorStateManager?.result?.updateDistributorMapVisibility(false);
                await data.distributorSelectorStateManager?.result?.setSelectedDistributorId(undefined);
            } else if (this._isWarehouseMapActive()) {
                await data.warehouseLocatorStateManager?.result?.setSelectedNodeKey(undefined);
            } else {
                await data.storeSelectorStateManager?.result?.setSelectedStoreLocationId(undefined);
            }
        });
    };

    private readonly _updateMapsForDistributor = (atlas: typeof import('azure-maps-control')) => {
        const pushpinOptions = this.props.config.pushpinOptions;
        const distributorSelectorStateManager = this.props.data.distributorSelectorStateManager?.result;
        const distributorInfoList = distributorSelectorStateManager?.distributorList;
        const selectedDistributorId = distributorSelectorStateManager?.selectedDistributorId;

        if (this.map && ArrayExtensions.hasElements(distributorInfoList)) {
            let mapLocation: OrgUnitLocation | undefined;
            this.map.markers.clear();

            const distributorList = distributorInfoList.filter(distributor => distributor.OrgUnit !== undefined);

            for (const [index, distributorInfo] of distributorList.entries()) {
                const orgUnit = distributorInfo.OrgUnit;

                if (orgUnit?.Latitude && orgUnit.Longitude) {
                    const isLocationSelected = selectedDistributorId === orgUnit?.RecordId;
                    const options = this._getPushpinOptions(isLocationSelected, index, pushpinOptions);
                    const marker = new atlas.HtmlMarker({
                        position: [orgUnit.Longitude, orgUnit.Latitude],
                        color: isLocationSelected ? 'green' : 'red',
                        options
                    });

                    this.map.markers.add(marker);

                    if (isLocationSelected) {
                        mapLocation = orgUnit;
                    }
                }
            }
            if (mapLocation) {
                this.map.setCamera({ center: [mapLocation.Longitude, mapLocation.Latitude] });
            } else {
                // Get all markers from the map
                const markers = this.map.markers.getMarkers();

                // Extract valid positions
                const positions = markers
                    .map((marker: atlas.HtmlMarker): atlas.data.Position | undefined => marker.getOptions().position) // Extract position
                    .filter((pos: atlas.data.Position | undefined): pos is atlas.data.Position => !!pos); // Remove undefined values

                if (positions.length > 0) {
                    // Create a bounding box from the marker positions
                    const bounds = atlas.data.BoundingBox.fromPositions(positions);

                    // Set the camera to fit the bounding box
                    this.map.setCamera({ bounds: bounds });
                }
            }
        }
    };

    /**
     * Update the map.
     */
    private readonly _updateMap = (atlas: typeof import('azure-maps-control')) => {
        const { pushpinOptions } = this.props.config;
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const orgUnitStoreInformation = storeSelectorStateManager?.context?.orgUnitStoreInformation;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;
        const selectedStore = storeSelectorStateManager?.selectedStore ?? { StoreId: selectedStoreLocationId };

        if (this.map && orgUnitStoreInformation) {
            let mapLocation: OrgUnitLocation | undefined;
            this.map.markers.clear();
            const pushpins: atlas.HtmlMarker[] = [];

            const storeLocationList = orgUnitStoreInformation.filter(store => store.OrgUnitAvailability !== undefined);

            for (const [index, unitStoreInfo] of storeLocationList.entries()) {
                const storeLocation = unitStoreInfo.OrgUnitAvailability?.OrgUnitLocation;

                if (storeLocation?.Latitude && storeLocation.Longitude) {
                    const isSelectedLocation = isStoreInfoLocation(storeLocation, selectedStore);
                    const markerOptions = this._getPushpinOptions(isSelectedLocation, index, pushpinOptions);

                    const pushpin = new atlas.HtmlMarker({
                        position: [storeLocation.Longitude, storeLocation.Latitude],
                        htmlContent: markerOptions.icon,
                        color: markerOptions.color
                    });

                    this.map.markers.add(pushpin);

                    this.map.events.add('click', pushpin, () => {
                        this.handleClickEvent(storeLocation);
                    });

                    isSelectedLocation ? pushpins.unshift(pushpin) : pushpins.push(pushpin);

                    if (isSelectedLocation) {
                        mapLocation = storeLocation;
                    }
                }
            }

            if (mapLocation) {
                const currentLocation = [mapLocation.Longitude, mapLocation.Latitude];
                this.map.setCamera({ center: currentLocation, zoom: 14 });
            } else {
                // explicit type assertion
                const positions = pushpins.map(pin => pin.getOptions().position).filter(pos => pos !== undefined) as atlas.data.Position[];

                if (positions.length > 0) {
                    const bounds = atlas.data.BoundingBox.fromPositions(positions);
                    this.map.setCamera({ bounds, padding: 50 });
                }
            }
        }
    };

    /**
     * Plot the warehouse-locator results.
     *
     * Marker icons represent each result's node type. Results with missing or zero coordinates
     * are skipped.
     *
     * Note the coordinate order: Azure Maps takes `[longitude, latitude]` — the **reverse** of the
     * Bing module's `Location(latitude, longitude)`. Reversing it throws no error; it silently
     * plots into the wrong hemisphere.
     * @param atlas - The dynamically imported azure-maps-control module.
     */
    private readonly _updateMapForWarehouses = (atlas: typeof import('azure-maps-control')) => {
        if (!this._isWarehouseMapOwner()) {
            return;
        }

        const { pushpinOptions } = this.props.config;
        const warehouseLocatorStateManager = this.props.data.warehouseLocatorStateManager?.result;
        const nodes = warehouseLocatorStateManager?.nodes;
        const selectedNodeKey = warehouseLocatorStateManager?.selectedNodeKey;

        if (this.map && nodes) {
            let mapLocation: ICrossEntityInventoryNode | undefined;
            this.map.markers.clear();
            const pushpins: atlas.HtmlMarker[] = [];

            for (const [index, node] of nodes.entries()) {
                if (node.latitude && node.longitude) {
                    const nodeKey = getCrossEntityNodeKey(node);
                    const isSelectedLocation = !!selectedNodeKey && selectedNodeKey === nodeKey;
                    const markerOptions = this._getPushpinOptions(
                        isSelectedLocation,
                        index,
                        pushpinOptions,
                        node.nodeType ?? CrossEntityNodeType.warehouse
                    );

                    const pushpin = new atlas.HtmlMarker({
                        position: [node.longitude, node.latitude],
                        htmlContent: markerOptions.icon,
                        color: markerOptions.color
                    });

                    this.map.markers.add(pushpin);

                    this.map.events.add('click', pushpin, () => {
                        this.handleWarehousePushpinClick(nodeKey);
                    });

                    isSelectedLocation ? pushpins.unshift(pushpin) : pushpins.push(pushpin);

                    if (isSelectedLocation) {
                        mapLocation = node;
                    }
                }
            }

            if (mapLocation) {
                this.map.setCamera({ center: [mapLocation.longitude, mapLocation.latitude], zoom: 14 });
            } else {
                const positions = pushpins.map(pin => pin.getOptions().position).filter(pos => pos !== undefined) as atlas.data.Position[];

                if (positions.length > 0) {
                    const bounds = atlas.data.BoundingBox.fromPositions(positions);
                    this.map.setCamera({ bounds, padding: 50 });
                }
            }
        }
    };

    private readonly handleClickEvent = (location: OrgUnitLocation | undefined): void => {
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        storeSelectorStateManager?.setSelectedStoreLocationId(undefined);
        if (location?.OrgUnitNumber) {
            setTimeout(() => {
                storeSelectorStateManager?.setSelectedStoreLocationId(
                    location.OrgUnitNumber,
                    location.DataAreaId,
                    location.InventoryLocationId
                );
            }, AzureMap.TIMEOUT);
        }
    };

    /**
     * Handle the click event of a warehouse marker.
     * @param nodeKey - Cross-legal-entity key of the clicked warehouse.
     */
    private readonly handleWarehousePushpinClick = (nodeKey: string | undefined): void => {
        const warehouseLocatorStateManager = this.props.data.warehouseLocatorStateManager?.result;
        void warehouseLocatorStateManager?.setSelectedNodeKey(undefined);
        if (nodeKey) {
            setTimeout(() => {
                void warehouseLocatorStateManager?.setSelectedNodeKey(nodeKey);
            }, AzureMap.TIMEOUT);
        }
    };

    /**
     * Whether this map instance is free to serve the warehouse locator.
     *
     * Every map instance on the page resolves the shared warehouse-locator state manager, so a map
     * owned by the store or distributor selector would otherwise react to warehouse state and clear
     * its own markers.
     * @returns Whether no other selector currently owns this map.
     */
    private readonly _isWarehouseMapOwner = (): boolean => {
        const { data } = this.props;

        return (
            !data.distributorSelectorStateManager?.result?.isDistributorSelectorDialogOpen &&
            !data.storeSelectorStateManager?.result?.isDialogOpen
        );
    };

    /**
     * Whether this map instance should currently be plotting warehouse-locator results.
     * @returns Whether warehouse results should take precedence on this map.
     */
    private readonly _isWarehouseMapActive = (): boolean => {
        return this._isWarehouseMapOwner() && ArrayExtensions.hasElements(this.props.data.warehouseLocatorStateManager?.result?.nodes);
    };

    /**
     * Get the pushpin option.
     * @param isSelectedLocation - Is selected location.
     * @param index - Index.
     * @param pushpinOptions - PushpinOptions.
     * @returns - The pushpin options.
     */
    private readonly _getPushpinOptions = (
        isSelectedLocation: boolean,
        index: number,
        pushpinOptions?: IPushpinOptionsData,
        nodeType?: CrossEntityNodeType
    ) => {
        const text = nodeType === undefined && pushpinOptions?.showIndex ? (index + 1).toString() : undefined;

        const size = pushpinOptions?.size || 1;
        const color = isSelectedLocation && pushpinOptions?.selectionColor
            ? pushpinOptions.selectionColor
            : pushpinOptions?.color;
        const icon = this._getSvgIcon(size, color, text, nodeType);

        return {
            // Fallback if icon doesn't render properly
            color: color,
            icon
        };
    };

    private readonly _getSvgIcon = (size: number, color?: string, text?: string, nodeType?: CrossEntityNodeType) => {
        const baseValue: number = 32;
        const markerType = nodeType === CrossEntityNodeType.retailStore ? 'store' : 'warehouse';
        const nodeIcon =
            markerType === 'store'
                ? '<path fill="#ffffff" d="M96 174h174l-12 56H108l-12-56zm22 70h132v92H118v-92zm25 23v69h62v-69h-62z" />'
                : '<path fill="#ffffff" d="M91 224l92-65 92 65v112h-34v-73H125v73H91V224zm58 68h68v44h-68v-44z" />';
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${baseValue * size}" height="${baseValue * size}" viewBox="0 0 365 560"${
            nodeType === undefined ? '' : ` data-node-type="${markerType}"`
        }>
                    <path fill="${color}" d="M182.9,551.7c0,0.1,0.2,0.3,0.2,0.3S358.3,283,358.3,194.6c0-130.1-88.8-186.7-175.4-186.9 C96.3,7.9,7.5,64.5,7.5,194.6c0,88.4,175.3,357.4,175.3,357.4S182.9,551.7,182.9,551.7z" />
                    ${nodeType === undefined ? (text ? `<text x="185" y="280" style="font-size:220px;fill:#ffffff;" text-anchor="middle">${text}</text>` : '') : nodeIcon}
                </svg>`;
    };

    /**
     * Load the Azure Maps CSS dynamically.
     */
    private _loadCss(): void {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.css';
        link.type = 'text/css';
        document.head.appendChild(link);
    }
}

export default AzureMap;
